/**
 * Iter-163 — Brief-Me-On-This-Page button.
 *
 * One-click "tutor mode" that:
 *   1. Looks up the current page's moduleKey from the URL
 *   2. Fetches its Cognara prompts (first prompt = the "brief")
 *   3. Dispatches `cognara:open` global event with the prompt + persona
 *      → AppLayout opens the AgentChatPanel pre-seeded with that prompt
 *
 * Mount next to the page title:
 *   <BriefMeButton/>
 *
 * Auto-renders nothing when the current page has no Cognara entry.
 */
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2 } from 'lucide-react';
import { getModuleCognara } from '@/config/moduleCognara';

// Per-page module key resolver — mirrors AppLayout::resolveModuleKey but
// without pulling in the giant dict. Keeps this component standalone.
function pickModuleKey(pathname) {
  // Strip ID tails
  const segs = pathname.split('/').filter(Boolean);
  // 4-segment match first (e.g. /app/cld/adjudication/case)
  const base4 = '/' + segs.slice(0, 4).join('/');
  const base3 = '/' + segs.slice(0, 3).join('/');
  const base2 = '/' + segs.slice(0, 2).join('/');

  // Try direct hits in the suggestion registry
  for (const candidate of [pathname, base4, base3, base2]) {
    // The registry keys are simple module slugs — derive from URL tail
    const tail = candidate.split('/').pop();
    if (tail && getModuleCognara(tail)) return tail;
  }

  // Heuristic: /app/cld/<page> → cld-<page>
  const m = pathname.match(/^\/app\/cld\/([a-z0-9-]+)/);
  if (m) {
    const tail = m[1];
    // Map common patterns to our CLD registry keys
    const map = {
      'register': 'cld-register',
      'manual-initiation': 'cld-claim-initiation',
      'v2': 'cld-workbench',
      'portfolio-analytics': 'cld-portfolio-analytics',
      'predictive-risk': 'cld-predictive-risk',
      'fm-notice': 'cld-fm-notice',
      'fm-notice-v2': 'cld-fm-notice',
      'evidence-v2': 'cld-evidence',
      'evidence-repository': 'cld-evidence',
      'quantum-engine': 'cld-quantum',
      'quantum-workbench': 'cld-quantum',
      'ld-clause-library': 'cld-ld-clause-library',
      'ld-config': 'cld-ld-config',
      'ld-notice': 'cld-ld-notice',
      'negotiation-table': 'cld-negotiation',
      'enforcement-board': 'cld-enforcement',
      'adjudication': 'cld-adjudication',
      'adjudication-list': 'cld-adjudication',
      'recovery-ledger': 'cld-recovery',
      'enforcement-board': 'cld-enforcement',
    };
    if (map[tail]) return map[tail];
  }
  return null;
}

export function BriefMeButton({ className = '', label = 'Brief me on this page' }) {
  const { pathname } = useLocation();
  const [firing, setFiring] = useState(false);

  const { entry, key } = useMemo(() => {
    const k = pickModuleKey(pathname);
    return { entry: k ? getModuleCognara(k) : null, key: k };
  }, [pathname]);

  if (!entry) return null;

  const onClick = () => {
    setFiring(true);
    const prompt = entry.prompts?.[0] || `Explain how this page works.`;
    const persona = entry.persona || null;
    window.dispatchEvent(new CustomEvent('cognara:open', {
      detail: { prompt, persona, moduleKey: key },
    }));
    // Brief loading state so user sees the action register
    setTimeout(() => setFiring(false), 800);
  };

  return (
    <Button
      onClick={onClick}
      variant="outline"
      size="sm"
      className={`gap-1.5 border-amber-300 bg-amber-50/60 hover:bg-amber-100 text-amber-900 ${className}`}
      data-testid="brief-me-button"
      title={`Open AI tutor with: "${entry.prompts?.[0]}"`}
    >
      {firing ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Sparkles className="w-3.5 h-3.5" />}
      <span className="text-xs font-medium">{label}</span>
    </Button>
  );
}

export default BriefMeButton;
